"use client";

import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { type VariantProps, cva } from "class-variance-authority";
import { type JSX, forwardRef } from "react";
import { cn } from "../../..";
import { type ButtonVariantProps, button } from "./Button";
import { type IconProps, icon } from "./Icon";

export const anchor = cva(
	"appearance-none no-prose text-left inline-block motion-safe:transition focus:outline-none enter:underline",
	{
		variants: {
			color: {
				dark: "text-dark",
				white: "text-white",
				"primary-medium": "text-primary-medium",
			},
		},
		defaultVariants: {
			color: "primary-medium",
		},
	},
);

export type AnchorVariantProps = VariantProps<typeof anchor>;

type TypeProps =
	| {
			variant?: "link";
			color?: AnchorVariantProps["color"];
			size?: never;
	  }
	| {
			variant: NonNullable<ButtonVariantProps["variant"]>;
			color?: ButtonVariantProps["color"];
			size?: ButtonVariantProps["size"];
	  };

export type AnchorProps = IconProps & TypeProps;

export const Anchor = forwardRef<HTMLAnchorElement, AnchorProps & JSX.IntrinsicElements["a"]>(
	function AnchorRef(props, ref) {
		const {
			variant = "link",
			color,
			size,
			starticon,
			endicon,
			iconColor,
			iconSize,
			iconClassName,
			className,
			children,
			...remainingProps
		} = props;

		return (
			<a
				ref={ref}
				className={cn(
					variant === "link"
						? anchor({ color: color as AnchorVariantProps["color"] })
						: button({
								color: color as ButtonVariantProps["color"],
								size,
								variant,
							}),
					"cursor-pointer",
					className,
				)}
				rel={props.target === "_blank" && !props.rel ? "noopener" : props.rel}
				{...remainingProps}
			>
				{starticon && (
					<FontAwesomeIcon
						className={cn(icon({ color: iconColor }), { "mr-2": children }, iconClassName)}
						icon={starticon}
						size={iconSize === "default" ? undefined : (iconSize ?? "sm")}
						fixedWidth
					/>
				)}

				{children}

				{endicon && (
					<FontAwesomeIcon
						className={cn(icon({ color: iconColor }), { "ml-2": children })}
						icon={endicon}
						size={iconSize === "default" ? undefined : (iconSize ?? "sm")}
						fixedWidth
					/>
				)}
			</a>
		);
	},
);
